/**
 * Workspaces ("businesses") on the client.
 *
 * The active business is chosen per browser tab. `crm/index.html` builds the
 * Supabase client with an `x-workspace-id` header read from
 * `sessionStorage['hunts.workspace']`, falling back to the last choice in
 * `localStorage['hunts.lastWorkspace']`. The server (`hunts.current_workspace()`)
 * honours the header only for workspaces the caller belongs to, so a stale or
 * forged id degrades to the personal workspace and never leaks another tenant.
 *
 * Switching business = write both storage keys and reload, because the header is
 * fixed when the client is created.
 *
 * Per-business settings (`workspace_settings`) are cached on
 * `globalThis.__huntsWsSettings`, an object shared with the legacy host so
 * `getStages()` on both sides reads one source.
 */

import { supabase } from './supabase'

export const WORKSPACE_SESSION_KEY = 'hunts.workspace'
export const WORKSPACE_LAST_KEY = 'hunts.lastWorkspace'
export const STAGES_CHANGED_EVENT = 'hunts:stages-changed'

export type WorkspaceKind = 'personal' | 'business'
export type WorkspaceRole = 'owner' | 'admin' | 'member'

/** One row of `my_workspaces()`. */
export interface WorkspaceSummary {
  id: string
  name: string
  slug: string
  kind: WorkspaceKind
  role: WorkspaceRole
  is_current: boolean
  contact_count: number
  leads_this_week: number
  sends_today: number
  created_at: string
}

/** One entry of `workspace_overview().businesses`. */
export interface OverviewBusiness {
  id: string
  name: string
  kind: WorkspaceKind
  role: WorkspaceRole
  created_at: string
  contacts: number
  accounts: number
  leads_7d: number
  leads_30d: number
  sends_today: number
  sends_7d: number
  sends_30d: number
  replies_7d: number
  open_deals: number
  pipeline_value: number
  open_tasks: number
}

export interface OverviewTotals {
  businesses: number
  contacts: number
  accounts: number
  leads_7d: number
  leads_30d: number
  sends_today: number
  sends_7d: number
  sends_30d: number
  replies_7d: number
  open_deals: number
  pipeline_value: number
  open_tasks: number
}

export interface WorkspaceOverview {
  totals: OverviewTotals
  businesses: OverviewBusiness[]
}

export interface Site {
  id: string
  workspace_id: string
  name: string
  domain: string
  kind: 'publication' | 'company' | 'event' | 'other'
  created_at: string
}

// ── storage ──────────────────────────────────────────────────────────────

function readStorage(kind: 'session' | 'local', key: string): string | null {
  try {
    const store = kind === 'session' ? globalThis.sessionStorage : globalThis.localStorage
    return store ? store.getItem(key) : null
  } catch {
    return null
  }
}

function writeStorage(kind: 'session' | 'local', key: string, value: string | null): void {
  try {
    const store = kind === 'session' ? globalThis.sessionStorage : globalThis.localStorage
    if (!store) return
    if (value === null) store.removeItem(key)
    else store.setItem(key, value)
  } catch {
    // private mode / quota: the header just falls back to the personal workspace
  }
}

/** The business this tab is using: tab choice first, then the last choice on this device. */
export function getActiveWorkspaceId(): string | null {
  return (
    readStorage('session', WORKSPACE_SESSION_KEY) ||
    readStorage('local', WORKSPACE_LAST_KEY) ||
    null
  )
}

/** Remember a business without reloading (used when the server already agrees). */
export function rememberWorkspace(id: string): void {
  writeStorage('session', WORKSPACE_SESSION_KEY, id)
  writeStorage('local', WORKSPACE_LAST_KEY, id)
}

export function clearActiveWorkspace(): void {
  writeStorage('session', WORKSPACE_SESSION_KEY, null)
  writeStorage('local', WORKSPACE_LAST_KEY, null)
}

/** Switch business: remember it and reload so every request carries the new header. */
export function setActiveWorkspace(id: string, reload = true): void {
  rememberWorkspace(id)
  if (reload && typeof location !== 'undefined') location.reload()
}

// ── RPC wrappers ─────────────────────────────────────────────────────────

function num(v: unknown): number {
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : 0
}

export async function fetchMyWorkspaces(): Promise<WorkspaceSummary[]> {
  const { data, error } = await supabase.rpc('my_workspaces')
  if (error) throw new Error(error.message)
  const rows = (data ?? []) as Array<Record<string, unknown>>
  return rows.map((r) => ({
    id: String(r.id),
    name: String(r.name),
    slug: String(r.slug),
    kind: r.kind === 'personal' ? 'personal' : 'business',
    role: (r.role as WorkspaceRole) ?? 'member',
    is_current: r.is_current === true,
    contact_count: num(r.contact_count),
    leads_this_week: num(r.leads_this_week),
    sends_today: num(r.sends_today),
    created_at: String(r.created_at ?? ''),
  }))
}

export async function createBusiness(name: string): Promise<{ id: string; name: string }> {
  const { data, error } = await supabase.rpc('create_business', { p_name: name })
  if (error) throw new Error(error.message)
  const row = (Array.isArray(data) ? data[0] : data) as { id?: string; name?: string } | undefined
  if (!row?.id) throw new Error('create_business returned no row')
  return { id: row.id, name: row.name ?? name }
}

export async function renameBusiness(id: string, name: string): Promise<void> {
  const { error } = await supabase.rpc('rename_business', { p_id: id, p_name: name })
  if (error) throw new Error(error.message)
}

export async function fetchOverview(): Promise<WorkspaceOverview> {
  const { data, error } = await supabase.rpc('workspace_overview')
  if (error) throw new Error(error.message)
  const raw = (data ?? {}) as {
    totals?: Record<string, unknown>
    businesses?: Array<Record<string, unknown>>
  }
  const t = raw.totals ?? {}
  return {
    totals: {
      businesses: num(t.businesses),
      contacts: num(t.contacts),
      accounts: num(t.accounts),
      leads_7d: num(t.leads_7d),
      leads_30d: num(t.leads_30d),
      sends_today: num(t.sends_today),
      sends_7d: num(t.sends_7d),
      sends_30d: num(t.sends_30d),
      replies_7d: num(t.replies_7d),
      open_deals: num(t.open_deals),
      pipeline_value: num(t.pipeline_value),
      open_tasks: num(t.open_tasks),
    },
    businesses: (raw.businesses ?? []).map((b) => ({
      id: String(b.id),
      name: String(b.name),
      kind: b.kind === 'personal' ? 'personal' : 'business',
      role: (b.role as WorkspaceRole) ?? 'member',
      created_at: String(b.created_at ?? ''),
      contacts: num(b.contacts),
      accounts: num(b.accounts),
      leads_7d: num(b.leads_7d),
      leads_30d: num(b.leads_30d),
      sends_today: num(b.sends_today),
      sends_7d: num(b.sends_7d),
      sends_30d: num(b.sends_30d),
      replies_7d: num(b.replies_7d),
      open_deals: num(b.open_deals),
      pipeline_value: num(b.pipeline_value),
      open_tasks: num(b.open_tasks),
    })),
  }
}

// ── per-business settings cache ──────────────────────────────────────────

type WsSettings = Record<string, unknown>

function settingsStore(): WsSettings {
  const g = globalThis as { __huntsWsSettings?: WsSettings }
  if (!g.__huntsWsSettings) g.__huntsWsSettings = {}
  return g.__huntsWsSettings
}

/** The business's pipeline stages, or null when it has none of its own. */
export function getWorkspaceStages(): string[] | null {
  const v = settingsStore().stages
  if (Array.isArray(v) && v.length > 0 && v.every((s) => typeof s === 'string')) {
    return v as string[]
  }
  return null
}

/** Update the shared cache and tell the host page so it can re-render. */
export function setWorkspaceStagesCache(stages: string[] | null): void {
  const store = settingsStore()
  if (stages && stages.length > 0) store.stages = stages
  else delete store.stages
  try {
    globalThis.dispatchEvent(new CustomEvent(STAGES_CHANGED_EVENT, { detail: stages }))
  } catch {
    // no window (tests without jsdom globals)
  }
}

/** Load every `workspace_settings` row of the active business into the cache. */
export async function loadWorkspaceSettings(): Promise<void> {
  const { data, error } = await supabase.from('workspace_settings').select('key,value')
  if (error) throw new Error(error.message)
  const store = settingsStore()
  for (const k of Object.keys(store)) delete store[k]
  for (const row of (data ?? []) as Array<{ key: string; value: unknown }>) {
    store[row.key] = row.value
  }
}

/**
 * Save the business's stages (owner/admin only, enforced by RLS). An empty list
 * removes the row so the business falls back to the user's own stages.
 */
export async function saveWorkspaceStages(workspaceId: string, stages: string[]): Promise<void> {
  const cleaned = stages.map((s) => s.trim()).filter((s) => s.length > 0)
  if (cleaned.length === 0) {
    const { error } = await supabase
      .from('workspace_settings')
      .delete()
      .eq('workspace_id', workspaceId)
      .eq('key', 'stages')
    if (error) throw new Error(error.message)
    setWorkspaceStagesCache(null)
    return
  }
  const { error } = await supabase.from('workspace_settings').upsert(
    {
      workspace_id: workspaceId,
      key: 'stages',
      value: cleaned,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'workspace_id,key' },
  )
  if (error) throw new Error(error.message)
  setWorkspaceStagesCache(cleaned)
}

/** Test-only helper. */
export function _resetWorkspaceForTests(): void {
  const store = settingsStore()
  for (const k of Object.keys(store)) delete store[k]
}
